import React, { useEffect, useState } from 'react';
import SidebarLayout from '../../components/SidebarLayout';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessNotification from '../../components/SuccessNotification';
import TableSkeleton from '../../components/TableSkeleton';
import '../VisualsPage/styles.css';
import '../ViewDataPage/styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const ViewDataPage = () => {
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    fetchSchemas();
  }, []);

  const fetchSchemas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/visuals/schemas`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schemas');
      }

      const data = await response.json();
      setSchemas(data.data || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchTableData = async (schemaId, { useSkeleton = false } = {}) => {
    if (!schemaId) return;
    if (useSkeleton) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/visuals/data?schema_id=${schemaId}&limit=${pageSize}&page=${page}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch table data');
      }

      const data = await response.json();
      setTableData(data.data.records || []);
      if (data.data?.totalRecords != null) setTotalRecords(data.data.totalRecords);
      if (data.data?.page != null) setPage(data.data.page);
      if (data.data?.pageSize != null) setPageSize(data.data.pageSize);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      if (useSkeleton) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSchemaChange = (e) => {
    const schemaId = e.target.value;
    setSelectedSchema(schemaId);
    setPage(1);
    if (schemaId) {
      fetchTableData(schemaId);
    } else {
      setTableData([]);
    }
  };

  useEffect(() => {
    if (!selectedSchema) return;
    fetchTableData(selectedSchema, { useSkeleton: true });
  }, [page]);

  const getTableColumns = () => {
    if (tableData.length === 0) return [];
    const firstRecord = tableData[0];
    if (firstRecord && firstRecord.record_data) {
      return Object.keys(firstRecord.record_data);
    }
    return [];
  };

  const getIsAllSelected = () => tableData.length > 0 && selectedIds.size === tableData.length;
  const toggleSelectAll = () => {
    if (getIsAllSelected()) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set((tableData || []).map(r => r.record_id)));
    }
  };
  const toggleSelectOne = (recordId) => {
    const next = new Set(selectedIds);
    if (next.has(recordId)) next.delete(recordId); else next.add(recordId);
    setSelectedIds(next);
  };

  const handleDeleteClick = () => {
    if (selectedIds.size === 0) return;
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setShowConfirmModal(false);
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${API_URL}/visuals/data/delete`;
      const body = { schema_id: selectedSchema, record_ids: Array.from(selectedIds) };
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!resp.ok) throw new Error(await resp.text() || 'Failed to delete records');
      const result = await resp.json();
      const deletedCount = result.deletedCount ?? result.deleted ?? selectedIds.size;
      const failedIds = result.failedIds || [];
      if (failedIds.length > 0) {
        setSuccessMessage(`Deleted ${deletedCount}, failed ${failedIds.length}: ${failedIds.slice(0,5).join(', ')}${failedIds.length>5?'...':''}`);
      } else {
        setSuccessMessage(`Successfully deleted ${deletedCount} ${deletedCount === 1 ? 'record' : 'records'}`);
      }
      setShowSuccess(true);
      setTimeout(async () => {
        await fetchTableData(selectedSchema, { useSkeleton: true });
      }, 500);
    } catch (err) {
      setError(err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
      setRefreshing(false);
    }
  };

  const renderTableData = () => {
    const columns = getTableColumns();
    
    if (columns.length === 0) {
      return (
        <div className="visuals-empty-state">
          No data available for the selected schema
        </div>
      );
    }

    return (
      <div className="visuals-table-container">
        <table className="visuals-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={getIsAllSelected()}
                  onChange={toggleSelectAll}
                  aria-label="Select all rows"
                />
              </th>
              {columns.map((column, index) => (
                <th key={index}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((record, index) => (
              <tr key={record.record_id || index}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(record.record_id)}
                    onChange={() => toggleSelectOne(record.record_id)}
                    aria-label={`Select row ${index + 1}`}
                  />
                </td>
                {columns.map((column, colIndex) => (
                  <td key={colIndex}>
                    {record.record_data[column] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <SidebarLayout>
      <div className="visuals-container">
        <div className="visuals-content">
          {/* Data View Section */}
          <div className="visuals-section">
            <h2>View Data</h2>
            <div className="visuals-form-group">
              <label>Select Table to View:</label>
              <select 
                value={selectedSchema} 
                onChange={handleSchemaChange}
                className="visuals-select"
              >
                <option value="">Choose a table...</option>
                {schemas.map((schema) => (
                  <option key={schema.schema_id} value={schema.schema_id}>
                    {schema.table_name}
                  </option>
                ))}
              </select>
            </div>

            {(loading || refreshing) && selectedSchema && (
              <TableSkeleton columns={getTableColumns().length ? getTableColumns() : ['Col A','Col B','Col C','Col D','Col E']} rowCount={5} />
            )}

            {!loading && !refreshing && selectedSchema && (
              <div className="visuals-data-section">
                {tableData.length > 0 ? (
                  <>
                    <div className="visuals-data-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        {totalRecords > 0
                          ? `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalRecords)} of ${totalRecords}`
                          : 'No records'}
                      </span>
                      <div style={{ display: 'inline-flex', gap: 8 }}>
                        <button 
                          className="danger-btn"
                          onClick={handleDeleteClick}
                          disabled={selectedIds.size === 0 || deleting || refreshing}
                          title={selectedIds.size === 0 ? 'Select rows to delete' : 'Delete selected rows'}
                        >
                          {deleting ? 'Deleting...' : `Delete Selected (${selectedIds.size})`}
                        </button>
                        <button
                          className="view-data-back-btn"
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page <= 1 || refreshing || deleting}
                        >
                          Prev
                        </button>
                        <button
                          className="add-data-btn"
                          onClick={() => setPage(p => (p * pageSize < totalRecords ? p + 1 : p))}
                          disabled={page * pageSize >= totalRecords || refreshing || deleting}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                    {renderTableData()}
                  </>
                ) : (
                  <div className="visuals-empty-state">
                    No data found for the selected table
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Records"
        message={`Are you sure you want to delete ${selectedIds.size} selected ${selectedIds.size === 1 ? 'record' : 'records'}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        icon="🗑️"
        loading={deleting}
      />
      <SuccessNotification
        isVisible={showSuccess}
        message={successMessage}
        onClose={() => setShowSuccess(false)}
        duration={4000}
      />
    </SidebarLayout>
  );
};

export default ViewDataPage;
